const numericSize = (value, fallback = 1) => {
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

const scaledOffset = (value, scale) => {
  if (typeof value === 'number') return `${value * scale}px`

  const parsed = Number.parseFloat(value)
  if (!Number.isFinite(parsed)) return '0rem'

  const unit = String(value).trim().replace(String(parsed), '') || 'px'
  return `${parsed * scale}${unit}`
}

/** Coerce CMS / JSON offsets to a CSS length (empty → fallback). */
const coerceOffset = (value, fallback = '0rem') => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `${value}rem`
  }
  if (typeof value === 'string' && value.trim()) {
    return value.trim()
  }
  return fallback
}

const HERO_VARIANTS = {
  // CSS vars: --hero-image-* / --hero-size-factor (outletTemplate.module.css)
  outlet: {
    prefix: 'hero',
    refWidth: 29,
    mobileWidthScale: 0.55,
  },
  // CSS vars: --ind-hero-image-* / --ind-hero-size-factor (UAEPageComponents.module.css)
  industry: {
    prefix: 'ind-hero',
    refWidth: 26,
    mobileWidthScale: 0.65,
  },
}

/**
 * Shared CSS variables for hero floating images.
 * Images start at the center of the hero canvas, then move by x/y.
 * Optional mobile: { x, y, width } overrides on small screens.
 *
 * Apply this style on `.heroImageFrame` (the element that uses the CSS
 * `translate` with these vars). Outer GSAP containers should stay free of
 * these vars so scale/parallax transforms do not fight position offsets.
 *
 * @param {object} image
 * @param {'outlet'|'industry'} [variant='outlet']
 */
export const getHeroImageStyle = (image = {}, variant = 'outlet') => {
  const config = HERO_VARIANTS[variant] || HERO_VARIANTS.outlet
  const { prefix, refWidth, mobileWidthScale } = config
  const mobile = image.mobile || {}
  const defaultWidth = variant === 'industry' ? '41rem' : '40rem'
  const defaultHeight = variant === 'industry' ? '40rem' : '30rem'
  const width =
    typeof image.width === 'string' && image.width.trim()
      ? image.width.trim()
      : coerceOffset(image.width, defaultWidth)
  const height =
    typeof image.height === 'string' && image.height.trim()
      ? image.height.trim()
      : coerceOffset(image.height, defaultHeight)
  const widthNum = numericSize(width, Number.parseFloat(defaultWidth) || 40)
  const heightNum = numericSize(height, Number.parseFloat(defaultHeight) || 30)
  const sizeFactor = widthNum / refWidth
  const x = coerceOffset(image.x)
  const y = coerceOffset(image.y)
  const mobileX = coerceOffset(mobile.x, '')
  const mobileY = coerceOffset(mobile.y, '')

  return {
    [`--${prefix}-image-width`]: width,
    [`--${prefix}-image-mobile-width`]:
      (typeof mobile.width === 'string' && mobile.width.trim()) ||
      scaledOffset(width, mobileWidthScale),
    [`--${prefix}-size-factor`]: String(
      Number.isFinite(sizeFactor) && sizeFactor > 0 ? sizeFactor : 1
    ),
    [`--${prefix}-image-ratio`]: `${widthNum} / ${heightNum}`,
    [`--${prefix}-image-x`]: x,
    [`--${prefix}-image-y`]: y,
    [`--${prefix}-image-mobile-x`]: mobileX || scaledOffset(x, 0.42),
    [`--${prefix}-image-mobile-y`]: mobileY || scaledOffset(y, 0.7),
    zIndex: image.zIndex ?? image.zindex,
  }
}

/** Industry hero — same helper with industry CSS var prefix. */
export const getIndHeroImageStyle = (image = {}) => getHeroImageStyle(image, 'industry')

const hasOffset = (value) =>
  value !== undefined && value !== null && String(value).trim() !== ''

/**
 * True when the image uses classic absolute placement (top/bottom/left/right)
 * instead of the centered x/y system. Used by water-park-style heroes.
 */
export const hasAbsoluteHeroPlacement = (image = {}) => {
  if (!image) return false
  const hasAbs = ['top', 'bottom', 'left', 'right'].some((key) =>
    hasOffset(image[key])
  )
  const hasXY = ['x', 'y'].some((key) => hasOffset(image[key]))
  return hasAbs && !hasXY
}

/** Inline styles for absolute-placed hero floats (tubes, etc.). */
export const getAbsoluteHeroImageStyle = (image = {}) => {
  const z = image.zIndex ?? image.zindex
  return {
    width: image.width,
    height: image.height,
    left: image.left,
    right: image.right,
    top: image.top,
    bottom: image.bottom,
    ...(z != null ? { '--hero-float-z': z, zIndex: z } : {}),
  }
}

/** OutletTemplate float images — position + optional zIndex (drives --hero-float-z). */
export const getOutletHeroFloatStyle = (image = {}) => {
  if (!image) return {}
  const z = image.zIndex ?? image.zindex
  return {
    width: image.width,
    height: image.height,
    left: image.left,
    right: image.right,
    top: image.top,
    bottom: image.bottom,
    ...(z != null ? { '--hero-float-z': z, zIndex: z } : {}),
  }
}

/**
 * Decorative absolute images (e.g. water-park tubes with zIndex) stay put —
 * no scroll parallax on the container.
 */
export const isStaticHeroImage = (image = {}) =>
  hasAbsoluteHeroPlacement(image) &&
  (image.zIndex != null || image.zindex != null)
